import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import heroPanels from "@/assets/hero-panels.webp";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";

import portfolioTotem from "@/assets/portfolio-totem.png";
import portfolioPvc from "@/assets/portfolio-pvc.png";
import portfolioSagomato from "@/assets/portfolio-sagomato.png";
import portfolioSegnaletica from "@/assets/portfolio-segnaletica.png";
import portfolioDecorativo from "@/assets/portfolio-decorativo.png";
import portfolioEspositore from "@/assets/portfolio-espositore-banco.png";

const carouselImages = [
  { src: portfolioTotem, alt: "Espositori Large In Cartone" },
  { src: portfolioPvc, alt: "Pannello Forex Stampato" },
  { src: portfolioSagomato, alt: "Totem in Cartone Sagomato" },
  { src: portfolioSegnaletica, alt: "Segnaletica da Interno" },
  { src: portfolioEspositore, alt: "Espositore da banco" },
  { src: portfolioDecorativo, alt: "Pannello Decorativo" },
];

const Hero = () => {
  const navigate = useNavigate();
  const { getLocalizedPath, language } = useLanguage();
  const { t } = useTranslation();
  const hero = t("hero");
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const handleInteraction = () => {
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };
  
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-36 pb-16">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroPanels} 
          alt={language === "it" 
            ? "Pannelli ed espositori in cartone - produzione professionale" 
            : "Cardboard panels and displays - professional production"
          }
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1080}
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-background/40" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Text Content */}
          <div className="max-w-2xl">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 text-foreground leading-[1.1] tracking-tight"
            >
              {language === "it" ? (
                <>
                  Pannelli e Insegne Personalizzate,
                  <br />
                  <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent">
                    Su Misura, per Te
                  </span>
                </>
              ) : (
                <>
                  Custom Panels & Signs,
                  <br />
                  <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent">
                    Made Just for You
                  </span>
                </>
              )}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
              className="text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed max-w-xl"
            >
              {language === "it" 
                ? "Produciamo pannelli, insegne ed espositori personalizzati in diversi materiali. Dalla progettazione alla realizzazione, trasformiamo le tue idee in soluzioni creative e funzionali."
                : "We produce custom panels, signs and display stands in various materials. From design to production, we transform your ideas into creative and functional solutions."
              }
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
              className="flex flex-col gap-3 sm:max-w-xs"
            >
              <Button 
                onClick={() => navigate(getLocalizedPath("/contatti"))} 
                size="default"
                variant="accent"
                className="shadow-lg hover:shadow-xl"
              >
                {hero.ctaContact}
              </Button>
              <Button 
                onClick={() => navigate(getLocalizedPath("/preventivo"))} 
                size="default"
                variant="outline" 
                className="group"
              >
                {hero.ctaConfigurator}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>

          {/* Right: Carousel */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
            className="relative w-full max-w-xl mx-auto lg:mx-0 lg:ml-auto"
          >
            <div 
              className="relative aspect-square rounded-2xl overflow-hidden shadow-2xl bg-background cursor-pointer"
              onClick={() => navigate(getLocalizedPath("/portfolio"))}
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              {/* Images */}
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={carouselImages[currentIndex].src}
                  alt={carouselImages[currentIndex].alt}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />

              {/* Navigation Arrows - Left & Right - Always visible */}
              <button
                onClick={(e) => { e.stopPropagation(); handleInteraction(); prevSlide(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-background transition-colors border border-border"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-4 h-4 text-foreground" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleInteraction(); nextSlide(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-background transition-colors border border-border"
                aria-label="Next image"
              >
                <ChevronRight className="w-4 h-4 text-foreground" />
              </button>

              {/* Caption */}
              <motion.div
                key={`caption-${currentIndex}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-0 left-0 right-0 p-4"
              >
                <p className="text-white text-base font-semibold drop-shadow-lg">
                  {carouselImages[currentIndex].alt}
                </p>
              </motion.div>

              {/* Dots Indicator */}
              <div className="absolute bottom-4 right-4 flex gap-1.5">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => { e.stopPropagation(); handleInteraction(); setCurrentIndex(index); }}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      index === currentIndex 
                        ? "bg-white w-4" 
                        : "bg-white/50 hover:bg-white/70"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
